// The small pieces every screen uses. No library, no abstraction tax.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { config } from "../config";

export function Panel({
  eyebrow,
  aside,
  children,
  tone = "raised",
  className = ""
}: {
  eyebrow?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  tone?: "raised" | "sunk" | "quiet";
  className?: string;
}): JSX.Element {
  const toneClass = tone === "sunk" ? " panel--sunk" : tone === "quiet" ? " panel--quiet" : "";
  return (
    <section className={`panel${toneClass} ${className}`.trim()}>
      {eyebrow ? (
        <p className="eyebrow">
          <span className="eyebrow__rest">{eyebrow}</span>
          {aside}
        </p>
      ) : null}
      {children}
    </section>
  );
}

export function Field({
  label,
  hint,
  children
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
    </label>
  );
}

export function Note({
  title,
  children,
  tone = "signal"
}: {
  title: string;
  children?: ReactNode;
  tone?: "signal" | "settled" | "halt" | "quiet";
}): JSX.Element {
  const toneClass = tone === "signal" ? "" : ` note--${tone}`;
  return (
    <div className={`note${toneClass}`}>
      <p className="note__title">{title}</p>
      {children ? <p className="note__body">{children}</p> : null}
    </div>
  );
}

/**
 * A capability, on screen, with one button that puts it on the
 * clipboard. It is never a link the page navigates to and it is never in
 * a query string — the value here is a fragment carrier, and the whole
 * point of a fragment is that nothing transmits it.
 */
export function Copyable({
  label,
  value,
  hint,
  href
}: {
  label: string;
  value: string;
  hint?: ReactNode;
  /** Set only when following it is meaningful — a customer's own link. */
  href?: string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  const copy = useCallback(() => {
    void navigator.clipboard?.writeText(value).then(
      () => setCopied(true),
      () => setCopied(false)
    );
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1800);
  }, [value]);

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  return (
    <div className="field">
      <span className="field__label">{label}</span>
      <div className="copyline">
        <code className="copyline__value wf-code" title={value}>{value}</code>
        <button type="button" className="button button--small" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </button>
        {href ? (
          <a className="button button--small" href={href} target="_blank" rel="noreferrer">
            Open
          </a>
        ) : null}
      </div>
      {hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}

interface QrBundle {
  encodeQr(data: string, options?: { ecLevel?: string; maxVersion?: number }): unknown;
  qrSvg(symbol: unknown, options?: { margin?: number }): string;
}

let qrBundle: Promise<QrBundle> | null = null;

function loadQr(): Promise<QrBundle> {
  if (!qrBundle) {
    const base = config.engineBaseUrl.endsWith("/") ? config.engineBaseUrl : `${config.engineBaseUrl}/`;
    qrBundle = import(/* @vite-ignore */ new URL(`${base}qr.browser.js`, location.href).href) as Promise<QrBundle>;
  }
  return qrBundle;
}

/**
 * A symbol for the other phone's camera.
 *
 * §20.5's policy is `maxVersion: 25` at EC level M: past that a symbol is
 * technically valid and practically unscannable off a screen at arm's
 * length. When a payload will not fit inside that, this says so and
 * points at the file — which is the honest answer, because a hundred-drop
 * day never fit a QR and still does not.
 *
 * A route-day ticket (§21.11) costs a flat 157 bytes more for the
 * certificate it carries, which is about eight address-only stops in a
 * scannable symbol against twelve without. A school route or a milk run
 * still scans; the fallback below is where anything larger goes, and it
 * is the same fallback and the same bytes as any other job.
 */
export function Qr({ value, alt }: { value: string; alt: string }): JSX.Element {
  const [svg, setSvg] = useState<string | null>(null);
  const [tooBig, setTooBig] = useState(false);

  useEffect(() => {
    let live = true;
    setSvg(null);
    setTooBig(false);
    loadQr()
      .then(bundle => {
        if (!live) return;
        try {
          setSvg(bundle.qrSvg(bundle.encodeQr(value, { ecLevel: "M", maxVersion: 25 }), { margin: 3 }));
        } catch {
          setTooBig(true);
        }
      })
      .catch(() => { if (live) setTooBig(true); });
    return () => { live = false; };
  }, [value]);

  if (tooBig) {
    return (
      <Note title="Too big for a code anyone's camera can read" tone="quiet">
        This round is past the size a phone can scan off a screen. Send the file instead — it is the
        identical sealed bytes.
      </Note>
    );
  }
  if (!svg) return <div className="qr" aria-hidden="true" />;
  return (
    <div className="qr" role="img" aria-label={alt} dangerouslySetInnerHTML={{ __html: svg }} />
  );
}

/** Downloads text as a file, without a server and without a navigation. */
export function downloadFile(name: string, contents: string, type = "application/octet-stream"): void {
  const blob = new Blob([contents], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
